import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import dynamicfunction from '../../../utils/dynamicfunction';
import { convertIntoArray, getContentKeyAccToLang, showToast } from '../../../utils/utils';
import { Checkbox, ErrorText, RowView, Text } from '../../CorporateForm/components';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Button from '../../../components/Button';
import { logoutUser } from '../../../store/actions/authActions';
import CustomCheckbox from '../../../components/CustomCheckbox';
import { individual_client_classification_validation_schema } from '../../../validations_schemas/individual_client_classification_schema';
import {
  getIndClientClassificationData,
  postIndClientClassification,
  postIndDocumentClientClassification,
  saveIndKycClientClassification
} from '../../../store/actions/individualAction';
import { fontSizes } from '../../../constants/constants';
import KeepValuesOnLanguageChange from '../../../components/KeepValuesOnLanguageChange';

const ClientClassification = ({ onDone }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isArabic } = dynamicfunction();

  const { individualContent, language } = useSelector((state) => state.content);
  const {
    ind_post_loading,
    ind_client_classification_data,
    ind_client_classification_dropdown_data,
  } = useSelector((state) => state.individual);

  const {
    client_application_id,
    is_kyc,
  } = useSelector((state) => state.constant);

  // Unsaved form values captured on language switch; in memory only, so a hard reload clears them.
  const draftValues = useRef(null);

  const getContent = (key) => {
    return getContentKeyAccToLang(
      individualContent?.client_classification?.data?.attributes,
      key,
      isArabic
    );
  };

  const myArray = convertIntoArray(ind_client_classification_dropdown_data);

  useEffect(() => {
    getData();
  }, [language]);

  const getData = async () => {
    await dispatch(
      getIndClientClassificationData(client_application_id)
    );
  };

  const onSubmit = (values) => {
    const criterias = values?.clientClassificationAnyEntityCriterias || [];
    const hasNoneOfAbove = criterias.some(item => Number(item.valueId) === 1573);

    if (!hasNoneOfAbove) {
      const checkDocument = criterias.length === (values?.documentsList?.length || 0);
      if (!checkDocument) {
        showToast({
          icon: 'error',
          title: getContent('please_upload_all_documents'),
        });
        return;
      }
    }

    const api_data = {
      ...ind_client_classification_data,
      clientApplicationId: client_application_id,
      investmentCriteria: values?.clientClassificationAnyEntityCriterias?.map(
        (item) => item?.valueId
      ) ?? null,
      // clientClassificationAnyEntityCriterias: values?.clientClassificationAnyEntityCriterias ?? null,
      iConfirmIHaveReadTheAboveInformation:
        values?.agreeToTermsAndCondition ?? false,
    };

    dispatch(
      is_kyc ?
        saveIndKycClientClassification(api_data, (res) => {
          values?.isSaveExist ? navigate('/funds/available-funds') : onDone();
        }) :
        postIndClientClassification(api_data, (res) => {
          values?.isSaveExist ? dispatch(logoutUser()) : onDone();
        }),
    );
  };

  const onDeleteDocument = (option, values, setFieldValue) => {
    let array = [...values?.documentsList];
    setFieldValue(
      'documentsList',
      array?.filter((val) => val?.investmentCriteriaId?.toString() !== option?.id?.toString())
    );
  };

  // const investmentCriteriaOptions =
  //   ind_client_classification_dropdown_data?.investmentCriteria
  //     ? Object.entries(
  //       ind_client_classification_dropdown_data.investmentCriteria
  //     ).map(([id, item]) => ({
  //       id: Number(id),
  //       name: item.displayName,
  //       helperText: item.helperText,
  //     }))
  //     : [];

  const uploadDocument = (option, setFieldValue, values) => {
    const selectedId = option?.id?.toString();

    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.pdf,.png,.jpg,.jpeg';
      input.style.display = 'none';

      input.onchange = (event) => {
        const file = event.target.files?.[0];

        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        const apiData = {
          params: `?clientApplicationId=${client_application_id}&selectionItemId=${selectedId}`,
          data: formData,
        };

        dispatch(
          postIndDocumentClientClassification(apiData, (res) => {
            let array = values?.documentsList?.length > 0 ? [...values?.documentsList] : [];

            const documentIndex = array?.findIndex((val) => val?.investmentCriteriaId === selectedId);

            if (documentIndex !== -1) {
              array[documentIndex] = {
                ...array[documentIndex],
                documentName: file?.name,
                documentMimeType: file?.type,
              };
            } else {
              array.push({
                investmentCriteriaId: selectedId,
                documentName: file?.name,
                documentMimeType: file?.type,
              });
            }
            setFieldValue('documentsList', array);
          })
        );
      };

      document.body.appendChild(input);
      input.click();
      document.body.removeChild(input);
    } catch (error) { }
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={draftValues.current ? draftValues.current : {
          clientClassificationAnyEntityCriterias:
            myArray?.investmentCriteria
              ?.filter(item =>
                ind_client_classification_data?.investmentCriteria
                  ?.includes(Number(item.id))
              )
              ?.map(item => ({
                typeOfEntityCriteriaId: 0,
                valueId: Number(item?.id),
              })) ?? [],
          sourceOfFundForThisAccount:
            ind_client_classification_data?.sourceOfFundForThisAccount?.map(
              item => item?.toString(),
            ) ?? [],
          agreeToTermsAndCondition:
            ind_client_classification_data?.iConfirmIHaveReadTheAboveInformation ?? false,
          documentsList:
            ind_client_classification_data?.documentsList ?? [],
        }}
        validationSchema={individual_client_classification_validation_schema}
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ handleSubmit, setFieldValue, values, resetForm, setFieldError, errors, touched, setFieldTouched }) => {
          return (
            <div className=''>
              <KeepValuesOnLanguageChange onLanguageChange={(currentValues) => (draftValues.current = currentValues)} />
              <div>
                <Checkbox
                  label={getContent('does_any_conditions_apply')}
                  value={values?.clientClassificationAnyEntityCriterias}
                  onChange={(option) => {
                    let updated_arr = [...(values?.clientClassificationAnyEntityCriterias || [])];

                    const exists = updated_arr?.some((item) => item?.valueId === option?.id);
                    let updatedDocuments = [...(values?.documentsList || [])];

                    if (option?.id === 1573) {
                      updated_arr = [
                        {
                          typeOfEntityCriteriaId: 0,
                          valueId: 1573
                        },
                      ];
                      updatedDocuments = []
                    } else if (exists) {
                      updated_arr = updated_arr?.filter((item) => item?.valueId !== option?.id);
                      updatedDocuments = updatedDocuments.filter(
                        doc => doc?.investmentCriteriaId?.toString() !== option?.id?.toString(),
                      );
                    } else {
                      updated_arr = updated_arr?.filter((item) => item?.valueId !== 1573);
                      updated_arr.push({
                        typeOfEntityCriteriaId: 0,
                        valueId: option?.id,
                      });
                    }

                    setFieldValue('clientClassificationAnyEntityCriterias', updated_arr);
                    setFieldTouched('clientClassificationAnyEntityCriterias', false);
                    setFieldValue('documentsList', updatedDocuments);
                  }}
                  options={
                    myArray?.investmentCriteria
                      ? Object.entries(
                        ind_client_classification_dropdown_data.investmentCriteria
                      ).map(([id, item]) => ({
                        id: Number(id),
                        name: item.displayName,
                        helperText: item.helperText,
                      }))
                      : []
                  }
                  isVertical
                  isUploadDocument={!values?.clientClassificationAnyEntityCriterias?.some((item) => item?.valueId?.toString() === '1573')}
                  uploadedDocuments={values?.documentsList}
                  onPressDocument={(option) => uploadDocument(option, setFieldValue, values)}
                  onDeleteDocument={(option) => onDeleteDocument(option, values, setFieldValue)}
                  getContent={getContent}
                />
              </div>

              <RowView>
                <ErrorText
                  text={getContent(
                    touched?.clientClassificationAnyEntityCriterias
                      ? errors?.clientClassificationAnyEntityCriterias
                      : ''
                  )}
                />
              </RowView>
              <div className='pt-3 sm:pt-5'>
                <RowView className={'gap-1 items-center'}>
                  <ErrorOutlineIcon sx={{ color: 'red' }} />
                  <Text
                    text={getContent('conditions_warning')}
                    size={fontSizes.h14}
                    color='text-red'
                    isTruncate={false} />
                </RowView>
              </div>

              <RowView className='pt-3 sm:pt-5 w-full'>
                <CustomCheckbox
                  isChecked={values?.agreeToTermsAndCondition}
                  label={getContent('understand_and_agree')}
                  secondText={getContent('terms_conditions')}
                  onClick={() => {
                    setFieldValue('agreeToTermsAndCondition', !values?.agreeToTermsAndCondition);
                    setFieldTouched('agreeToTermsAndCondition', false);
                  }}
                  onPressSecondText={() => {
                    setFieldValue('agreeToTermsAndCondition', !values?.agreeToTermsAndCondition);
                    setFieldTouched('agreeToTermsAndCondition', false);
                  }}
                />
              </RowView>
              <RowView>
                <ErrorText
                  text={getContent(
                    touched?.agreeToTermsAndCondition
                      ? errors?.agreeToTermsAndCondition
                      : ''
                  )}
                />
              </RowView>

              <div className='w-full h-0.5 bg-border mt-3 sm:mt-5'></div>
              <RowView className='gap-3 sm:gap-7 pt-3 sm:pt-5'>
                <Button
                  text={getContent('save_proceed')}
                  color={'text-white hover:text-primary '}
                  bgColor='bg-primary hover:bg-transparent hover:border-primary'
                  className={'border border-transparent w-44 px-0 text-center'}
                  isDisabled={ind_post_loading}
                  loading={ind_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', false);
                    handleSubmit();
                  }}
                />
                <Button
                  text={getContent('save_exit')}
                  color={'text-primary hover:text-white'}
                  bgColor='bg-white hover:bg-primary'
                  className={'border border-primary w-44 px-0 text-center'}
                  isDisabled={ind_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', true);
                    handleSubmit();
                  }}
                />
              </RowView>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default ClientClassification;
